import { useState } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { BUY_URL, X_URL } from "../constants";
import { XIcon } from "./XIcon";
import { SmartImage } from "./SmartImage";

const links = [
  { label: "Home", href: "#top" },
  { label: "The Legend", href: "#legend" },
  { label: "Gallery", href: "#gallery" },
  { label: "Clips", href: "#clips" },
  { label: "Airdrops", href: "#airdrop" },
  { label: "The Call", href: "#the-call" },
  { label: "Chart", href: "#chart" },
  { label: "Bull Posts", href: "#bull-posts" },
  { label: "Storm History", href: "#storm-history" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050409]/60 border-b border-[#9945FF]/20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <SmartImage
            src="/assets/logo.png"
            alt="CURB logo"
            eager
            className="w-9 h-9 rounded-full border border-[#14F195]/50 group-hover:rotate-12 transition-transform duration-300"
          />
          <span className="font-marker text-xl gradient-sol">$CURB</span>
        </a>

        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-bebas text-lg tracking-widest text-zinc-300 hover:text-[#14F195] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-x-button"
            className="btn-ghost-x rounded-full p-2.5"
            aria-label="Curb on X"
          >
            <XIcon size={16} />
          </a>
          <a
            href={BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-buy-button"
            className="btn-buy font-bebas tracking-widest text-lg rounded-full px-5 py-1.5"
          >
            BUY $CURB
          </a>
          <button
            type="button"
            data-testid="nav-hamburger-button"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-zinc-300 hover:text-[#14F195] transition-colors p-1"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <div
              data-testid="nav-mobile-backdrop"
              className="md:hidden fixed inset-0 top-16 bg-black/40"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.18 }}
              data-testid="nav-mobile-menu"
              className="md:hidden absolute top-16 right-3 w-52 rounded-xl border border-[#9945FF]/30 bg-[#0a0714]/95 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] overflow-hidden"
            >
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block font-bebas text-base tracking-widest text-zinc-300 hover:text-[#14F195] hover:bg-[#9945FF]/10 px-5 py-2.5 border-b border-[#9945FF]/10 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={BUY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-buy"
                className="block font-bebas text-base tracking-widest text-[#14F195] hover:bg-[#14F195]/10 px-5 py-2.5 border-b border-[#9945FF]/10 transition-colors"
              >
                Buy $CURB
              </a>
              <a
                href={X_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-x"
                className="flex items-center gap-2 font-bebas text-base tracking-widest text-zinc-300 hover:text-[#14F195] hover:bg-[#9945FF]/10 px-5 py-2.5 transition-colors"
              >
                <XIcon size={13} /> X / Twitter
              </a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};
